import Logo from "./../../assets/logo-login.svg";
import Button from "../../components/Buttons/Button";
import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { secondary } from "../../theme";
import { useApi } from "../../hooks/api";
import { useForm } from "react-hook-form";
import { BsArrowLeft } from "react-icons/bs";
import { useGlobal } from "../../hooks/global";
import { IGroupProps } from "../../interfaces";
import { ModalLoading } from "../../components/Loadings/ModalLoading";
import { GenericForm, IGenericFormRefProps } from "../../components/GenericForm";
import { validateCEP, validateCNPJ, validateEmail, validatePhone } from "../../utils/validatesFields";
import { Container, ContainerBottomButtons, ContainerImage, ContainerLink, ContainerTop, Description, Form, Link, Title, LogoImg, Content, ContainerTerms, AnchorTerms, Span } from "./style";
import { Flex, Progress, Text } from "@mantine/core";
import { managedAccommodationsOptions, positionTypeOptions } from "../../utils/options";
import zxcvbn from "zxcvbn";
import { FiAlertCircle } from "react-icons/fi";
import { Box } from "@material-ui/core";
import { MagicButton } from "../../components/Buttons/MagicButton";
import DEFAULT_LOGO from '../../assets/web-check-in-default-logo.png';

const terms_url = "https://webcheckin.app/termos-de-uso/"

interface INewAccountProps { }

export const NewAccount: React.FC<INewAccountProps> = () => {
    const [loading, setLoading] = useState(false)
    const [loadingCEP, setLoadingCEP] = useState(false)
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [acceptTerms, setAcceptTerms] = useState(false);

    const { register, handleSubmit, control, watch, formState: { errors }, setError, setValue, trigger } = useForm({
        mode: "all"
    });

    const _form = watch()

    const form_ref = useRef<IGenericFormRefProps>(null)
    const buttonSubmitRef = useRef<HTMLButtonElement>(null)

    const { notify, setTheme, theme } = useGlobal()
    const { api, signIn, consultsServices } = useApi();

    const changeCEP = useCallback(async (search) => {
        search = search.replace(/\D/g, "")
        if (search.length === 8) {
            setLoadingCEP(true)

            const { address, city, state } = await consultsServices.address({ search })

            if (address) {
                setValue("street", address.logradouro)
                setValue("neighborhood", address.bairro)
                setValue("address_complement", address.complemento)
            }

            if (city) setValue("city_id", { label: city.name, value: city.id, this: city })

            if (state) setValue("state_id", { label: state.name, value: state.id, initials: state.initials, this: state })

            setLoadingCEP(false)

            trigger()
        }
    }, [])

    const changePassword = useCallback((password) => {
        const score = zxcvbn(password).score

        setPasswordStrength(score);
    }, []);

    const getStrengthLabel = (score: number) => {
        const labels = ["Muito fraca", "Fraca", "Média", "Boa", "Forte"];
        return labels[score];
    };

    const getStrengthColor = (score: number) => {
        const colors = ["gray", "red", "yellow", "green", "teal"];
        return colors[score];
    };

    const strengthLabel = getStrengthLabel(passwordStrength);
    const strengthColor = getStrengthColor(passwordStrength);

    const transformDefaultLogoInBase64 = useCallback(async () => {
        try {
            const response = await fetch(DEFAULT_LOGO);
            const blob = await response.blob();

            const file = new File([blob], "web-check-in-default-logo.png", { type: blob.type });

            const formData = new FormData();
            formData.append("photo", file);

            const { data } = await api.post("/photos/transform", formData);

            return data.complete
        } catch (err) {
            console.error(err);
        }
    }, []);

    const onSubmit = useCallback(async (event) => {
        setLoading(true)

        const form = form_ref.current?.getForm();

        const logo = await transformDefaultLogoInBase64();

        const {
            city_id,
            legal_name,
            managed_accommodations,
            state_id,
            trade_name,
            zip_code,
            cnpj,
            email,
            surname,
            password,
            phone,
            position,
        } = form;

        const business = {
            legal_name,
            trade_name,
            cnpj,
            zip_code,
            state_id,
            city_id,
            managed_accommodations,
            logo,
        }

        const responsible = {
            surname,
            position,
            phone,
            email,
            password,
        }

        const data = { business, responsible }

        try {

            await api.post("/create-new-account", data)

            await signIn({ email, password })

        } catch (err: any) {
            const error = err.response ? err.response.data : "SERVER ERROR";

            console.error("ERROR", error)
            if (error.field) {
                setError(error.field, { type: "manual", message: error.detail });
            }

           // if (error.path) setError(error.path, { type: "manual", message: error.message })
           if(error?.title) notify(error.title, "error")
            else notify("ERRO INTERNO DO SISTEMA", "error")
        }

        setLoading(false)

    }, [form_ref, api, _form])

    const handleStopPropagation = useCallback((e: FormEvent<HTMLFormElement>) => {
        e?.stopPropagation()
        handleSubmit(onSubmit)(e)
    }, [handleSubmit, onSubmit, form_ref, _form, api])

    const groups: IGroupProps[] = [
        {
            name: "",
            label: "",
            fields: [
                [
                    {
                        name: "legal_name",
                        label: "Razão Social",
                        type: "input",
                        required: true
                    }
                ],
                [
                    {
                        name: "trade_name",
                        label: "Nome Fantasia",
                        type: "input",
                        required: true
                    }
                ],
                [
                    {
                        name: "cnpj",
                        label: "CNPJ",
                        type: "input",
                        mask: "cnpj",
                        validate: validateCNPJ,
                        required: true
                    },
                    {
                        name: "zip_code",
                        label: "CEP",
                        type: "input",
                        mask: "cep",
                        validate: validateCEP,
                        executeOnChange: changeCEP,
                        required: true,
                    },
                ],
                [
                    {
                        name: "state_id",
                        label: "Estado",
                        type: "select-single-no-creatable",
                        isClearable: true,
                        searchOptions: consultsServices.state_id,
                        executeOnChange: async () => setValue("city_id", null),
                        required: true,
                    },
                    {
                        name: "city_id",
                        label: "Cidade",
                        type: "select-single-no-creatable",
                        isClearable: true,
                        searchOptions: consultsServices.city_id,
                        additionalsQueries: (data: any) => ({ state_id: data?.state_id?.value }),
                        getIsDisabled: (data: any) => !data?.state_id,
                        required: true,
                    }
                ],
                [
                    {
                        name: "surname",
                        label: "Responsável legal",
                        type: "input",
                        required: true
                    }
                ],
                [
                    {
                        name: "position",
                        label: "Cargo",
                        type: "select-fixed",
                        options: positionTypeOptions,
                        required: true
                    },
                    {
                        name: "managed_accommodations",
                        label: "Acomodações administradas",
                        type: "select-fixed",
                        options: managedAccommodationsOptions,
                    }
                ],
                [
                    {
                        name: "phone",
                        label: "Telefone",
                        type: "input",
                        mask: "phone",
                        validate: validatePhone,
                        required: true
                    },
                    {
                        name: "email",
                        label: "Email",
                        type: "input",
                        validate: validateEmail,
                        required: true
                    }
                ],
                [
                    {
                        name: "password",
                        label: "Senha",
                        type: "input",
                        required: true,
                        isPassword: true,
                        noUpperCase: true,
                        executeOnChange: async (e) => changePassword(e),
                    },
                    {
                        name: "confirm-password",
                        label: "Repetir senha",
                        type: "input",
                        required: true,
                        isPassword: true,
                        validate: async (value: any) => {
                            const password = watch("password")
                            if (value !== password) return "As senhas não conferem."
                            return true
                        },
                        noUpperCase: true
                    }
                ]
            ]
        }
    ]

    useEffect(() => {
        if (theme != 'light') {
            setTheme('light')
        }
    }, [])

    return (
        <Container>

            <ContainerImage />

            <Content>
                <ContainerTop>
                    <ContainerLink>
                        <Link
                            to="/login"
                            children={(
                                <>
                                    <BsArrowLeft />
                                    Voltar para o login
                                </>
                            )}
                        />
                    </ContainerLink>

                    <Flex pb='5px'>
                        <LogoImg src={Logo} height={38} />
                    </Flex>

                    <Title>
                        Preencha o formulário e
                        experimente <Span>gratuitamente</Span>.
                    </Title>

                    <Description>
                        Preencha o formulário e faça um teste grátis de 7 dias.
                        Descubra como a nossa plataforma pode ajudar a sua empresa!
                    </Description>

                </ContainerTop>

                <Form onSubmit={handleStopPropagation}>
                    <GenericForm
                        ref={form_ref}
                        groups={groups}
                        _form={_form}
                        control={control}
                        trigger={trigger}
                        errors={errors}
                        setValue={setValue}
                        register={register}
                        disabled={loadingCEP}
                    />

                    <Box mt={3}>
                        <Progress
                            value={(passwordStrength + 1) * 20}
                            color={strengthColor}
                            mt="xs"
                            radius="xl"
                            sx={{ transition: "width 4.5s ease-in-out, background-color 4.3s ease-in-out" }}
                        />

                        <Text size="sm" color={strengthColor} mt={4} display="flex" style={{ alignItems: 'center', fontSize: 13, fontFamily: 'Poppins' }}>
                            <FiAlertCircle size={16} style={{ marginRight: 3 }} />
                            {strengthLabel}
                        </Text>
                    </Box>

                    <div
                        style={{
                            marginTop: 30,
                            marginLeft: -5,
                            display: "flex",
                            gap: 5,
                            alignItems: "flex-start",
                            cursor: "pointer",
                        }}
                        onClick={() => setAcceptTerms((old) => !old)}
                    >
                        <MagicButton
                            checked={acceptTerms}
                            onClick={() => setAcceptTerms((old) => !old)}
                            theme="light"
                            type="checkbox"
                            label={""}
                            labelStyle={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 13,
                            }}
                            background={secondary.light}
                        />
                        <div
                            style={{
                                marginTop: -11,
                                cursor: "pointer",
                                userSelect: "none",
                                fontSize: 13,
                            }}
                        >
                            Ao criar sua conta, você concorda com os
                            <AnchorTerms
                                target="_blank"
                                href={terms_url}
                                children={<Span> termos de uso</Span>}
                            />
                            .
                        </div>
                    </div>

                    <button
                        ref={buttonSubmitRef}
                        type="submit"
                        style={{ display: "none" }}
                    />
                </Form>

                <ContainerBottomButtons>
                    <Button
                        color="#FFF"
                        background={secondary.light}
                        onClick={() => buttonSubmitRef.current?.click()}
                        style={{ fontSize: 15, padding: "8px 16px" }}
                        children="Criar conta"
                        disabled={loadingCEP || loading || !acceptTerms}
                    />

                </ContainerBottomButtons>
            </Content>

            <ModalLoading loading={loading} theme="light" />

        </Container>
    )
}